import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AGENT, SERVICES } from "@/lib/site";
import ContactMethodPicker, { CONTACT_METHOD_LABEL } from "@/components/ContactMethodPicker";

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    contact_method: "email",
    topic: SERVICES[0].title,
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    // pri izbranem načinu kontakta moramo imeti vsaj eno ujemajočo se polje
    if (form.contact_method === "email" && !form.email) {
      toast.error("Vnesite e-naslov.");
      return;
    }
    if ((form.contact_method === "phone" || form.contact_method === "whatsapp") && !form.phone) {
      toast.error("Vnesite telefonsko številko.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", {
        name: form.name || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        contact_method: form.contact_method,
        topic: form.topic,
        message: form.message || undefined,
      });
      toast.success("Sporočilo poslano. Oglasil se vam bom v kratkem.");
      setForm({
        name: "",
        email: "",
        phone: "",
        contact_method: "email",
        topic: SERVICES[0].title,
        message: "",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const waUrl = `https://wa.me/${AGENT.whatsapp}?text=${encodeURIComponent(
    "Pozdravljeni, zanimajo me vaše zavarovalne storitve."
  )}`;

  const needsEmail = form.contact_method === "email";
  const needsPhone = form.contact_method === "phone" || form.contact_method === "whatsapp";

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-24 md:py-32"
      style={{ background: "var(--vp-surface)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-sm font-semibold tracking-widest uppercase text-[var(--vp-primary)] mb-3">
              Kontakt
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Pišite. Pokličite. Klepetajmo.
            </h2>
            <p className="mt-5 text-[var(--vp-muted)] leading-relaxed">
              Brezplačno svetovanje brez obveznosti. Najhitrejši odziv preko WhatsAppa.
            </p>

            <div className="mt-8 space-y-5">
              <ContactRow icon={<Phone size={18} />} label="Telefon">
                <a href={`tel:${AGENT.phone.replace(/\s/g, "")}`} data-testid="contact-phone" className="hover:text-[var(--vp-primary)]">
                  {AGENT.phone}
                </a>
              </ContactRow>
              <ContactRow icon={<MessageCircle size={18} />} label="WhatsApp">
                <a href={waUrl} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="hover:text-[var(--vp-primary)]">
                  Začnite klepet
                </a>
              </ContactRow>
              <ContactRow icon={<Mail size={18} />} label="E-pošta">
                <a href={`mailto:${AGENT.email}`} data-testid="contact-email" className="hover:text-[var(--vp-primary)]">
                  {AGENT.email}
                </a>
              </ContactRow>
              <ContactRow icon={<Clock size={18} />} label="Delovni čas">
                <div className="space-y-0.5">
                  <div>{AGENT.hoursInfo}</div>
                  <div className="text-sm font-semibold text-[var(--vp-primary)]">
                    {AGENT.hoursEmergency}
                  </div>
                </div>
              </ContactRow>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="rounded-2xl border border-[var(--vp-border)] bg-white p-7 md:p-9 space-y-5"
            >
              <div>
                <Label className="text-sm font-semibold">Kako vas naj kontaktiram?</Label>
                <p className="text-xs text-[var(--vp-muted)] mt-0.5 mb-3">
                  Izberite, kako želite, da se vam odzovem. {CONTACT_METHOD_LABEL[form.contact_method]} izbrano.
                </p>
                <ContactMethodPicker
                  value={form.contact_method}
                  onChange={(v) => setForm({ ...form, contact_method: v })}
                  testid="inquiry-method"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Ime in priimek</Label>
                  <Input
                    data-testid="inquiry-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5"
                    placeholder="Neobvezno"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">
                    Telefon{needsPhone ? " *" : ""}
                  </Label>
                  <Input
                    data-testid="inquiry-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    required={needsPhone}
                    className="mt-1.5"
                    placeholder={needsPhone ? "Obvezno" : "Neobvezno"}
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">
                    E-naslov{needsEmail ? " *" : ""}
                  </Label>
                  <Input
                    data-testid="inquiry-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required={needsEmail}
                    className="mt-1.5"
                    placeholder={needsEmail ? "Obvezno" : "Neobvezno"}
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Tema</Label>
                  <Select
                    value={form.topic}
                    onValueChange={(v) => setForm({ ...form, topic: v })}
                  >
                    <SelectTrigger data-testid="inquiry-topic" className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s.key} value={s.title}>{s.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Sporočilo</Label>
                <Textarea
                  data-testid="inquiry-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Kako vam lahko pomagam? (neobvezno)"
                  className="mt-1.5"
                />
              </div>
              <Button
                type="submit"
                data-testid="inquiry-submit"
                disabled={loading}
                className="w-full rounded-full py-6 text-base font-semibold"
                style={{ background: "var(--vp-primary)", color: "white" }}
              >
                {loading ? "Pošiljanje…" : "Pošlji povpraševanje"}
              </Button>
              <p className="text-xs text-[var(--vp-muted)] text-center">
                Vaši podatki se uporabljajo izključno za stik in svetovanje.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <div
        className="h-10 w-10 flex-shrink-0 rounded-xl flex items-center justify-center"
        style={{ background: "var(--vp-sand)", color: "var(--vp-primary)" }}
      >
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-[var(--vp-muted)] font-semibold">{label}</div>
        <div className="font-medium text-[var(--vp-text)] mt-0.5">{children}</div>
      </div>
    </div>
  );
}
